import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/contexts/PermissionContext';
import {
  canSeeSubAccountsEntry,
  parseSubAccountUsage,
  type SubAccountUsage,
} from '@/lib/subAccountsEntry';

/**
 * SUB-2.11 · FR63/FR5. One question, asked of the server: is the account I am in
 * a parent, and how much of its allowance is used?
 *
 * ⚠️ `resolved` is not `!loading`. It is "the server has answered", and the gate
 * must not be believed before it is true. A page that renders on `!loading` shows
 * its content during the first paint after a tenant switch, when `usage` is still
 * the previous account's answer or null. That one frame is an FR19 disclosure, so
 * the two are separate flags and the guard reads `resolved`.
 *
 * ⚠️ AND `resolved` IS NOT ENOUGH EITHER. Fixed 2026-08-12, after a superadmin
 * sitting in an enabled parent was redirected off the page.
 *
 * The RPC is one round trip and it starts the moment `currentTenant` exists.
 * That is exactly when PermissionContext BEGINS its own fetch: it holds
 * `loading: true` until auth, tenant and the permission query have all settled
 * (`PermissionContext.tsx:184`), and `isAgencyAdmin` arrives with TenantContext's
 * `user_roles` fetch. So the fast answer lands while both role terms are still
 * false, the gate reads "not a superadmin, not an Agency Admin", and the page
 * redirects before the viewer is known.
 *
 * The rule this cost us: a gate over N asynchronous inputs is only closed when
 * EVERY input has answered. Three of the four here start at their denied value,
 * so "not yet loaded" and "not allowed" are the same value and only the loading
 * flags tell them apart. `ready` is that conjunction, and it is what the page
 * and the sidebar consume.
 */
export interface SubAccountUsageState {
  usage: SubAccountUsage | null;
  /** Every input has answered: the RPC, the permission set, and the tenant. */
  resolved: boolean;
  error: string | null;
  /** FR63/FR19, already combined with the viewer's role. False until resolved. */
  canSeeEntry: boolean;
  refetch: () => Promise<void>;
}

export function useSubAccountUsage(): SubAccountUsageState {
  const {
    currentTenant,
    isAgencyAdmin,
    // SUB-2.90 · F-82. NOT `hasPermission('system.override')`. See the field's doc.
    hasSystemOverride,
    isAgencyAdminResolved,
    isLoading: tenantLoading,
  } = useTenant();
  const { loading: permissionsLoading } = usePermissions();
  const [usage, setUsage] = useState<SubAccountUsage | null>(null);
  const [rpcAnswered, setRpcAnswered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantId = currentTenant?.tenant_id ?? null;

  /**
   * ⚠️ THE ANSWER MUST BE DISCARDED IF IT ARRIVES FOR THE WRONG ACCOUNT.
   *
   * The comment on the effect below has always claimed that clearing state on a
   * tenant change stops the previous account's `enabled: true` guarding the next
   * account's page. Clearing alone does not: the in-flight call for account A was
   * never abandoned, so if A's response landed after B's it wrote A's answer into
   * B's state and opened the page on A's authority. Reachable without a reload,
   * because `returnToHomeTenant()` calls `refetch()` in place.
   *
   * Every request takes a sequence number; the effect's cleanup bumps it. A
   * response whose number is stale writes nothing, which covers the tenant switch,
   * a concurrent `refetch`, and unmount with one mechanism.
   */
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    const stale = () => seq !== requestSeq.current;

    if (!tenantId) {
      // ⚠️ ANSWERED, NOT PENDING. This used to leave `rpcAnswered` false, and
      // `resolved` is `rpcAnswered && rolesResolved`, so it could never become
      // true and the page spun forever with no redirect, no error and no timeout.
      // "There is no current tenant" is a real answer and it resolves to denied:
      // PermissionContext has a dedicated branch for `user && !currentTenant &&
      // !tenantLoading`, so this state is reachable and is not transient.
      setUsage(null);
      setError(null);
      setRpcAnswered(true);
      return;
    }

    try {
      const { data, error: rpcError } = await supabase.rpc('sub_account_usage', {
        p_tenant_id: tenantId,
      });

      if (stale()) return;

      if (rpcError) {
        // A refused or failed call is not "no sub-accounts"; it is "unknown".
        // Both close the gate, and only one of them is worth surfacing.
        setUsage(null);
        setError(rpcError.message);
        setRpcAnswered(true);
        return;
      }

      const parsed = parseSubAccountUsage(data);

      // A 200 carrying a shape we cannot read is not the same as a denial, and it
      // used to be indistinguishable from one: `usage` null with `error` null,
      // which reads as "resolved, not allowed" and redirects with nothing logged
      // in a production build.
      if (parsed === null) {
        setUsage(null);
        setError('The server returned a sub-account usage payload this build cannot read.');
        setRpcAnswered(true);
        return;
      }

      setUsage(parsed);
      setError(null);
      setRpcAnswered(true);
    } catch (e) {
      if (stale()) return;
      setUsage(null);
      setError(e instanceof Error ? e.message : 'Could not read sub-account usage');
      setRpcAnswered(true);
    }
  }, [tenantId]);

  useEffect(() => {
    // The answer belongs to one account. Clearing on tenant change is what stops
    // the previous account's `enabled: true` guarding the next account's page,
    // and the cleanup below is what makes that true rather than merely intended.
    setUsage(null);
    setRpcAnswered(false);
    setError(null);
    void load();
    return () => {
      requestSeq.current += 1;
    };
  }, [load]);

  const viewer = {
    // ⚠️ SUB-2.90 · F-82. From TenantContext, NOT `hasPermission('system.override')`.
    // It has to be the same answer that decided `isAgencyAdmin`, which the
    // superadmin branch of `fetchUserTenants` clears on the strength of it. Pairing
    // the permission with the flag made both false for a user holding Superadmin
    // through `user_roles`, and `/app/sub-accounts` redirected them away.
    hasSystemOverride,
    isAgencyAdmin,
    // The rule itself lives in canSeeSubAccountsEntry, where it is tested. This
    // only supplies the fact.
    //
    // ⚠️ `isAgencyAdminResolved` is the third term, and it is not redundant with
    // `!tenantLoading`. TenantContext clears `isLoading` in a `finally`, so a
    // degraded `user_roles` read — the embed fails, the fallback query succeeds,
    // and the Agency Admin marker is dropped — leaves loading false with the role
    // term wrong. Without this the gate reads "resolved, and the answer is no" and
    // redirects a real Agency Admin off their own page: the f04026a4 bug on a
    // different input.
    rolesResolved: !permissionsLoading && !tenantLoading && isAgencyAdminResolved,
  };

  // Every input has answered. Drives the spinner; the gate applies its own half.
  const ready = rpcAnswered && viewer.rolesResolved;

  // ⚠️ A DEGRADED ROLE READ NEEDS A TERMINAL STATE, NOT A LONGER WAIT.
  //
  // Requiring `isAgencyAdminResolved` fixes the confident-denial bug, but on its
  // own it converts that bug into a spinner that never ends, which is the other
  // half of the same lesson (an indeterminate input handled as "keep waiting",
  // with no bound). Both loading flags are false and the marker is still unread,
  // so this is not pending — it is failed, and it says so through `error`, which
  // the page renders before it decides anything about authority.
  const rolesDegraded = !permissionsLoading && !tenantLoading && !isAgencyAdminResolved;
  const effectiveError =
    error ??
    (rolesDegraded
      ? 'Your account roles could not be read, so this page cannot tell whether you may see it. Reload, and contact support if it persists.'
      : null);

  // A gate that hides things by design is hard to debug precisely because it is
  // silent, so in dev it says which input closed it. Never in a build.
  //
  // ⚠️ In an effect, not the render body. It ran on every render of a hook the
  // Sidebar calls on every page, and a conditional side effect in a render body is
  // the pattern React warns about under concurrent rendering.
  const debugHasSystemOverride = viewer.hasSystemOverride;
  const debugIsAgencyAdmin = viewer.isAgencyAdmin;
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.debug('[SUB-2.11] sub-accounts entry', {
      ready,
      rpcAnswered,
      permissionsLoading,
      tenantLoading,
      hasSystemOverride: debugHasSystemOverride,
      isAgencyAdmin: debugIsAgencyAdmin,
      usage,
      error,
    });
  }, [
    ready,
    rpcAnswered,
    permissionsLoading,
    tenantLoading,
    debugHasSystemOverride,
    debugIsAgencyAdmin,
    usage,
    error,
  ]);

  return {
    usage,
    resolved: ready,
    error: effectiveError,
    canSeeEntry: ready && canSeeSubAccountsEntry(viewer, usage),
    refetch: load,
  };
}
